import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { Strings } from "@app/constants";
import { FormProvider, StepperHeader } from "@app/components";
import { headersSteps } from "../stepperLabel";
import { Eligibility } from "./eligibility";
import {
  Brand,
  CarInfo,
  Images,
  KmDriven,
  MobileInfo,
  Model,
  SellingTime,
  Variant,
  Year,
} from "../steps";
import { CarValuation } from "./carValuation";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { sellCar } from "../slice";
import { useLocation, useNavigate } from "react-router-dom";
import { useRequestState } from "@app/hooks";
import { selectSellCarState } from "../selector";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { WarningBox } from "./warning";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

const SellCar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [getCarValue, setGetCarValue] = useState(false);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [openWarning, setOpenWarning] = useState(false);
  const [warningShownOnce, setWarningShownOnce] = useState(true);

  const { formValue } = location.state || [];
  const schema = Yup.object().shape({
    brandId: Yup.string().required("Brand required"),
    modelId: Yup.string().required("Model required"),
    year: Yup.string().required("Year required"),
    variant: Yup.string().required("Variant required"),
    KmDriven: Yup.string().required("KM required"),
    selectedSellingTime: Yup.string().required("Selling time required"),
    carInfo: Yup.object().shape({
      carCondition: Yup.string().required("Condition required"),
    }),
    image: Yup.object().shape({
      frontView: Yup.mixed().required("Image required"),
    }),
    mobile: Yup.string().required("Mobile required"),
  });
  const stepFields = [
    ["brandId"],
    ["modelId"],
    ["year"],
    ["variant"],
    ["KmDriven"],
    ["selectedSellingTime"],
    ["carInfo.carCondition"],
    ["image.frontView"],
    ["mobile"],
  ] as const;

  const methods = useForm({
    resolver: yupResolver(schema),
  });
  const formWatchedValues = methods.watch();

  const scrollToTop = () => {
    scrollBoxRef.current?.scrollIntoView();
  };

  useEffect(() => {
    scrollToTop();
  }, [activeStep]);

  useEffect(() => {
    window.history.pushState({ page: "sellCar" }, "", window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      if (warningShownOnce) {
        setOpenWarning(true);
      } else {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [warningShownOnce]);

  const handleOnSuccess = useCallback(() => {
    navigate(PATH_DASHBOARD.home);
  }, []);

  useRequestState({
    stateSelector: selectSellCarState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleOnSuccess,
  });

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setGetCarValue(false);
    }
  };

  const handleNext = async () => {
    const isValid = await methods.trigger(stepFields[activeStep] as any);

    if (!isValid) return;

    if (activeStep < headersSteps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };
  const convertToBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleSubmit = async (formData: any) => {
    const files = [
      formData?.image?.backView,
      formData?.image?.frontView,
      formData?.image?.leftSideView,
      formData?.image?.rightSideView,
      formData?.image?.interiorDashboard,
      formData?.image?.odometerReading,
    ];

    const fd = new FormData();

    files.forEach((file) => {
      if (file) fd.append("images", file);
    });

    fd.append("carNumber", formData?.carNumber || "");
    fd.append("brandId", formData?.brandId);
    fd.append("modelId", formData?.modelId);
    fd.append("engineType", formData?.engineType);
    fd.append("transmission", formData?.transmission);
    fd.append("manufacturingYear", formData?.year);
    fd.append("driven", formData?.KmDriven);
    fd.append("variant", String(formData?.variant));
    fd.append(
      "insuranceAvailable",
      (formData?.carInfo?.insurance?.isInsuranceAvailable === "Yes").toString(),
    );
    fd.append(
      "anyClaimsMade",
      (formData?.carInfo?.insurance?.anyClaimsMade === "Yes").toString(),
    );
    fd.append(
      "rcAvailable",
      (formData?.carInfo?.Ownership?.rcAvailable === "Yes").toString(),
    );
    fd.append("loanHypothecation", formData?.carInfo?.Ownership?.isLoan);
    fd.append("usageType", formData?.carInfo?.Ownership?.usageType);
    fd.append(
      "accidentHistory",
      formData?.carInfo?.accidentAndService?.accidentHistory,
    );
    fd.append(
      "serviceHistory",
      formData?.carInfo?.accidentAndService?.fullServiceRecords,
    );
    fd.append("carCondition", formData?.carInfo?.carCondition);
    fd.append("color", formData?.color);

    // Optional Date
    if (formData?.carInfo?.insurance?.expiryDate) {
      fd.append(
        "insuranceValidTill",
        new Date(formData.carInfo.insurance.expiryDate)
          .toISOString()
          .split("T")[0],
      );
    }

    // Final Dispatch
    dispatch(sellCar(fd));

    console.log("FORMDATA SENT →", Object.fromEntries(fd));
  };

  return (
    <>
      <FormProvider methods={methods}>
        {getCarValue ? (
          <Stack alignItems={"center"} justifyContent={"center"}>
            <Stack
              ref={scrollBoxRef}
              sx={{
                width: { xs: "96%", md: "80%" },
                alignSelf: "center",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 1,
                px: 2,
                py: 5,
                my: 5,
              }}
            >
              <CarValuation
                onBack={handleBack}
                methods={methods}
                handleSubmit={handleSubmit}
                formWatchedValues={formWatchedValues}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack ref={scrollBoxRef}>
            <StepperHeader
              headersSteps={headersSteps}
              activeStep={activeStep}
              onStepClick={setActiveStep}
            />

            <Stack
              sx={{
                px: { xs: 1, md: 2 },
                py: { xs: 1, md: 2 },
                alignItems: "center",
              }}
            >
              <Eligibility />
            </Stack>

            <Stack
              sx={{
                width: { xs: "96%", md: "80%" },
                alignSelf: "center",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              {activeStep === 0 && (
                <Brand methods={methods} formValue={formValue} />
              )}
              {activeStep === 1 && <Model methods={methods} />}
              {activeStep === 2 && <Year methods={methods} />}
              {activeStep === 3 && <Variant methods={methods} />}
              {activeStep === 4 && <KmDriven methods={methods} />}
              {activeStep === 5 && <SellingTime methods={methods} />}
              {activeStep === 6 && <CarInfo methods={methods} />}
              {activeStep === 7 && <Images methods={methods} />}
              {activeStep === 8 && (
                <MobileInfo
                  methods={methods}
                  setGetCarValue={setGetCarValue}
                  setActiveStep={setActiveStep}
                  handleSubmit={handleSubmit}
                />
              )}
            </Stack>
          </Stack>
        )}

        {activeStep <= 8 && (
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            px="10%"
            mt={4}
            mb={2}
          >
            <Button
              variant="contained"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {Strings.button.back}
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === headersSteps.length - 1}
            >
              {Strings.button.next}
            </Button>
          </Stack>
        )}
      </FormProvider>
      <WarningBox
        openWarning={openWarning}
        setOpenWarning={setOpenWarning}
        setWarningShownOnce={setWarningShownOnce}
      />
    </>
  );
};

export { SellCar };
